import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  EMPTY_MAP_REGION_CATALOG,
  fetchMapRegions,
  type MapRegionCatalog,
} from '@/api/geoRegions';
import { AUTH_UNAUTHORIZED_EVENT } from '@/api/client';

/**
 * App-wide turf catalog, fetched once per session from `GET /geo/regions`.
 *
 * ## Why a context and not a module-level object
 *
 * The obvious minimal change was to keep `MAP_REGIONS` as a mutable module
 * export and fill it in after the fetch resolved. That is a race with two
 * losing sides: every component that reads it during the first render sees
 * `{}` and, because a plain object mutation is not a React state change,
 * nothing re-renders when the data lands — the county map stays empty until
 * some unrelated state update happens to repaint it. A context makes the
 * arrival of turfs an ordinary render, and makes "still loading" a value
 * consumers can actually branch on instead of a shape they cannot distinguish
 * from "this tenant has none".
 *
 * It matches how the rest of this app holds shared server state (`AuthContext`,
 * `ThemeContext`) — no query library is in the dependency tree and this is not
 * the change that should add one.
 *
 * ## Why it is keyed on the token, not on `useAuth()`
 *
 * The provider deliberately does NOT read `AuthContext`. Turfs are wanted by
 * the same requests the token already authorizes, so the token is the honest
 * trigger; depending on the auth context would also force every test that
 * renders a turf-reading view to stand up an `AuthProvider` and a fake
 * `/auth/me`. `App.tsx` calls `refresh()` on a successful login (the demo
 * switcher and the invite flow both write the token directly, bypassing
 * `AuthContext.login`), and the 401 event clears the catalog on the way out so
 * the next tenant to log in on this browser never inherits the last one's
 * turfs.
 */
interface MapRegionsContextType {
  /** Never null, never Platte. Empty while loading and when unconfigured. */
  catalog: MapRegionCatalog;
  /** True while the first (or a refreshed) fetch is in flight. */
  loading: boolean;
  error: string | null;
  /**
   * The tenant has turfs loaded.
   *
   * FALSE while loading, so a consumer that renders an empty state on
   * `!configured` must check `loading` first or it will flash "not
   * configured" at every user on every boot. `/auth/me` also carries
   * `tenant_map_regions_configured`, which resolves earlier and lets the
   * county map skip the skeleton entirely — see `MapView`.
   */
  configured: boolean;
  /** Re-fetch. Called after a login that bypassed `AuthContext.login`. */
  refresh: () => Promise<void>;
}

export const MapRegionsContext = createContext<MapRegionsContextType | undefined>(
  undefined,
);

interface MapRegionsProviderProps {
  children: ReactNode;
}

export const MapRegionsProvider: React.FC<MapRegionsProviderProps> = ({
  children,
}) => {
  const [catalog, setCatalog] = useState<MapRegionCatalog>(
    EMPTY_MAP_REGION_CATALOG,
  );
  // Starts true only when there is a token to fetch with: a logged-out boot
  // must not park the app on a loading state that will never resolve.
  const [loading, setLoading] = useState<boolean>(
    () => !!localStorage.getItem('access_token'),
  );
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!localStorage.getItem('access_token')) {
      setCatalog(EMPTY_MAP_REGION_CATALOG);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setCatalog(await fetchMapRegions());
      setError(null);
    } catch (err) {
      // A failed fetch leaves the catalog EMPTY, which every consumer already
      // renders as "no turfs". The alternative — keeping a previous tenant's
      // catalog, or falling back to anything built in — is the bug this whole
      // change exists to remove.
      setCatalog(EMPTY_MAP_REGION_CATALOG);
      setError(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
          (err as Error)?.message ||
          'Could not load turfs.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Session gone → drop the turfs. The 401 interceptor has already cleared the
  // token, so this is about the in-memory copy: without it, logging out and
  // back in as another tenant on the same tab would render the old county's
  // turf names until the new fetch resolved.
  useEffect(() => {
    const handleUnauthorized = () => {
      setCatalog(EMPTY_MAP_REGION_CATALOG);
      setLoading(false);
      setError(null);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () =>
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const value = useMemo<MapRegionsContextType>(
    () => ({
      catalog,
      loading,
      error,
      configured: !loading && !catalog.isEmpty,
      refresh: load,
    }),
    [catalog, loading, error, load],
  );

  return (
    <MapRegionsContext.Provider value={value}>
      {children}
    </MapRegionsContext.Provider>
  );
};

export const useMapRegions = (): MapRegionsContextType => {
  const context = React.useContext(MapRegionsContext);
  if (!context) {
    throw new Error('useMapRegions must be used within MapRegionsProvider');
  }
  return context;
};
